import React, { useState, useEffect } from 'react';
import { 
    Shield, Zap, Lock, Globe, RefreshCcw, AlertTriangle, 
    Activity, ShieldCheck, Cpu, HardDrive, LayoutDashboard,
    Search, Filter, ExternalLink, ChevronRight, ShieldAlert
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "../ui/card";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Progress } from "../ui/progress";
import { Separator } from "../ui/separator";
import { 
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, 
    AlertDialogTitle, AlertDialogTrigger 
} from "../ui/alert-dialog";
import { 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, 
    ResponsiveContainer, Legend 
} from 'recharts';
import API from "../api/api01";
import { toast } from 'sonner';

const CloudflareCommandCenter: React.FC = () => {
    const [loading, setLoading] = useState<string | null>(null);
    const [stats, setStats] = useState({
        requests: '0',
        threats: '0',
        bandwidth: '0 GB',
        cacheHit: '0%'
    });
    const [securityStatus, setSecurityStatus] = useState({
        attack_mode: false,
        page_shield: false,
        bot_mgmt: false
    });
    const [isOnline, setIsOnline] = useState(false);

    const fetchData = async () => {
        try {
            const response = await API.get('/security/cloudflare/');
            const { analytics, settings, status } = response.data;
            
            setStats({
                requests: analytics.requests?.toLocaleString() || '0',
                threats: analytics.threats?.toLocaleString() || '0',
                bandwidth: `${(analytics.bandwidth / (1024 * 1024 * 1024)).toFixed(2)} GB`,
                cacheHit: `${analytics.cache_hit_rate?.toFixed(1)}%`
            });
            
            setSecurityStatus({
                attack_mode: settings.attack_mode || false,
                page_shield: settings.page_shield || false,
                bot_mgmt: settings.bot_mgmt || false
            });
            
            setIsOnline(status === 'online');
        } catch (error) {
            console.error("Failed to fetch Cloudflare data:", error);
        }
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 60000); // Refresh every minute
        return () => clearInterval(interval);
    }, []);

    const handleAction = async (action: string, value: any = true) => {
        setLoading(action);
        try {
            const response = await API.post('/security/cloudflare/', { action, value });
            if (response.data.status === 'success') {
                toast.success(response.data.message);
                await fetchData(); // Refresh data immediately
            } else {
                toast.error(response.data.message);
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || `Failed to perform ${action}`);
        } finally {
            setLoading(null);
        }
    };

    return (
        <div className="p-8 space-y-8 bg-[#f8fafc] dark:bg-slate-950 min-h-screen">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-blue-600 rounded-lg">
                            <Globe className="text-white w-6 h-6" />
                        </div>
                        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                            Edge Command Center
                        </h1>
                        <Badge variant={isOnline ? "outline" : "destructive"} className={isOnline ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400" : ""}>
                            <div className={`w-2 h-2 rounded-full mr-2 ${isOnline ? "bg-green-500 animate-pulse" : "bg-red-500"}`} />
                            {isOnline ? "EDGE ONLINE" : "EDGE OFFLINE"}
                        </Badge>
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 max-w-2xl">
                        Real-time infrastructure orchestration for XeroAlpha. Every toggle here affects global edge routing instantly.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button variant="outline" disabled={loading === 'sync_ips'}>
                                <RefreshCcw className={`mr-2 h-4 w-4 ${loading === 'sync_ips' ? 'animate-spin' : ''}`} />
                                Sync Origin Firewall
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                            <AlertDialogHeader>
                                <AlertDialogTitle>Restrict IP & Sync Firewall?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This will update the origin server's firewall to ONLY allow traffic from Cloudflare edge nodes. 
                                    Ensure your Cloudflare proxy (Orange Cloud) is active for all DNS records to avoid being locked out.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleAction('sync_ips')} className="bg-blue-600 hover:bg-blue-700">
                                    Continue
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => handleAction('purge_cache')} disabled={loading === 'purge_cache'}>
                        <Zap className={`mr-2 h-4 w-4 ${loading === 'purge_cache' ? 'animate-pulse' : ''}`} />
                        Purge Edge Cache
                    </Button>
                </div>
            </div>

            {/* Quick Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                    { label: "Total Requests (24h)", value: stats.requests, icon: Activity, color: "text-blue-600", bg: "bg-blue-50" },
                    { label: "Threats Mitigated", value: stats.threats, icon: ShieldCheck, color: "text-red-600", bg: "bg-red-50" },
                    { label: "Cache Hit Rate", value: stats.cacheHit, icon: Cpu, color: "text-amber-600", bg: "bg-amber-50" },
                    { label: "Edge Bandwidth", value: stats.bandwidth, icon: HardDrive, color: "text-green-600", bg: "bg-green-50" },
                ].map((stat, i) => (
                    <Card key={i} className="border-none shadow-sm dark:bg-slate-900">
                        <CardContent className="p-6 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">{stat.label}</p>
                                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">{stat.value}</h3>
                            </div>
                            <div className={`p-3 ${stat.bg} dark:bg-slate-800 rounded-xl`}>
                                <stat.icon className={`${stat.color} w-6 h-6`} />
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Tabs defaultValue="security" className="w-full">
                <TabsList className="bg-slate-200/50 dark:bg-slate-900 p-1 rounded-xl mb-8">
                    <TabsTrigger value="security" className="rounded-lg px-8">Security Stack</TabsTrigger>
                    <TabsTrigger value="performance" className="rounded-lg px-8">Optimization</TabsTrigger>
                    <TabsTrigger value="logs" className="rounded-lg px-8">Audit Logs</TabsTrigger>
                </TabsList>

                <TabsContent value="security" className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Security Controls */}
                        <div className="lg:col-span-2 space-y-6">
                            <Card className="border-none shadow-sm dark:bg-slate-900">
                                <CardHeader>
                                    <CardTitle>Core Security Modules</CardTitle>
                                    <CardDescription>Direct state control over Cloudflare edge protection</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
                                        <div className="flex items-center gap-4">
                                            <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
                                                <ShieldAlert className="text-red-600 w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="font-bold text-slate-900 dark:text-white">Under Attack Mode</p>
                                                <p className="text-sm text-slate-500">Forces JavaScript challenges globally to stop DDoS.</p>
                                            </div>
                                        </div>
                                        <Switch 
                                            checked={securityStatus.attack_mode} 
                                            onCheckedChange={(val) => handleAction('toggle_attack_mode', val)}
                                            disabled={loading !== null}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
                                        <div className="flex items-center gap-4">
                                            <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                                <ShieldCheck className="text-blue-600 w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="font-bold text-slate-900 dark:text-white">Page Shield</p>
                                                <p className="text-sm text-slate-500">Continuous monitoring of client-side scripts.</p>
                                            </div>
                                        </div>
                                        <Switch 
                                            checked={securityStatus.page_shield} 
                                            onCheckedChange={(val) => handleAction('toggle_page_shield', val)}
                                            disabled={loading !== null}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
                                        <div className="flex items-center gap-4">
                                            <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                                                <Cpu className="text-purple-600 w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="font-bold text-slate-900 dark:text-white">Advanced Bot Management</p>
                                                <p className="text-sm text-slate-500">Protects against sophisticated scraping and credential stuffing.</p>
                                            </div>
                                        </div>
                                        <Switch 
                                            checked={securityStatus.bot_mgmt} 
                                            onCheckedChange={(val) => handleAction('toggle_bot_mgmt', val)}
                                            disabled={loading !== null}
                                        />
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="border-none shadow-sm dark:bg-slate-900">
                                <CardHeader>
                                    <CardTitle>Recent Security Events</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        {[
                                            { event: "SQL Injection Blocked", ip: "192.168.1.1", country: "CN", time: "2m ago", risk: "Critical" },
                                            { event: "Bot Scraping Blocked", ip: "45.12.33.102", country: "RU", time: "15m ago", risk: "Medium" },
                                            { event: "SSH Brute Force", ip: "103.44.1.22", country: "BR", time: "1h ago", risk: "High" },
                                        ].map((item, i) => (
                                            <div key={i} className="flex items-center justify-between p-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
                                                <div className="flex items-center gap-3">
                                                    <Badge variant={item.risk === 'Critical' ? 'destructive' : 'outline'}>{item.risk}</Badge>
                                                    <div>
                                                        <p className="text-sm font-semibold text-slate-900 dark:text-white">{item.event}</p>
                                                        <p className="text-xs text-slate-500">{item.ip} • {item.country}</p>
                                                    </div>
                                                </div>
                                                <span className="text-xs text-slate-400">{item.time}</span>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                                <CardFooter className="bg-slate-50 dark:bg-slate-800/50 py-3 flex justify-center cursor-pointer hover:bg-slate-100 transition-all rounded-b-xl">
                                    <span className="text-sm font-medium text-blue-600 flex items-center gap-1">
                                        View Full Security Logs <ChevronRight className="w-4 h-4" />
                                    </span>
                                </CardFooter>
                            </Card>
                        </div>

                        {/* Edge Status Map (Visual Representation) */}
                        <Card className="border-none shadow-sm dark:bg-slate-900 flex flex-col">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Globe className="w-5 h-5 text-blue-500" />
                                    Global Edge Status
                                </CardTitle>
                                <CardDescription>Status of 310+ edge locations</CardDescription>
                            </CardHeader>
                            <CardContent className="flex-grow flex flex-col justify-center items-center text-center p-8">
                                <div className="relative w-48 h-48 mb-6">
                                    <div className="absolute inset-0 bg-blue-500/10 rounded-full animate-ping"></div>
                                    <div className="absolute inset-4 bg-blue-500/20 rounded-full animate-pulse"></div>
                                    <div className="absolute inset-10 bg-blue-500/30 rounded-full"></div>
                                    <Globe className="absolute inset-0 m-auto text-blue-600 w-24 h-24" />
                                </div>
                                <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Edge Network: ACTIVE</h4>
                                <p className="text-sm text-slate-500 mb-6">All global nodes are reporting healthy status. Zero latency anomalies detected.</p>
                                <div className="w-full space-y-4">
                                    <div className="space-y-1 text-left">
                                        <div className="flex justify-between text-xs font-medium mb-1">
                                            <span>Origin Latency</span>
                                            <span>42ms</span>
                                        </div>
                                        <Progress value={15} className="h-1.5 bg-slate-100" />
                                    </div>
                                    <div className="space-y-1 text-left">
                                        <div className="flex justify-between text-xs font-medium mb-1">
                                            <span>Edge Latency</span>
                                            <span>8ms</span>
                                        </div>
                                        <Progress value={5} className="h-1.5 bg-slate-100" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="performance">
                    <Card className="border-none shadow-sm dark:bg-slate-900">
                        <CardHeader>
                            <CardTitle>Network Performance Analytics</CardTitle>
                            <CardDescription>Monitor how Cloudflare is optimizing your delivery</CardDescription>
                        </CardHeader>
                        <CardContent className="h-[400px] flex items-center justify-center text-slate-400">
                            <div className="text-center">
                                <Activity className="w-12 h-12 mx-auto mb-4 opacity-20" />
                                <p>Performance metrics visualization placeholder</p>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default CloudflareCommandCenter;
